import { useState, useEffect } from "react";
import { Users, UserPlus, Trash2, Mail } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentManagement = ({ agent }) => {
  const [agents, setAgents] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "agent"
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents?requester_id=${agent.id}`);
      setAgents(response.data);
    } catch (error) {
      logger.error("Error fetching agents:", error);
      if (error.response?.status === 403) {
        alert("You don't have permission to view agents");
      }
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();

    // Email is now used for login instead of username
    if (!newAgent.name || !newAgent.email || !newAgent.password) {
      alert("Please fill all required fields");
      return;
    }

    if (newAgent.password !== newAgent.confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/agent/create`, {
        name: newAgent.name,
        email: newAgent.email,
        password: newAgent.password,
        confirm_password: newAgent.confirmPassword,
        role: newAgent.role,
        creator_id: agent.id
      });

      alert(`Agent ${newAgent.name} created successfully! They can login with their email: ${newAgent.email}`);
      setNewAgent({ name: "", email: "", password: "", confirmPassword: "", role: "agent" });
      setShowCreateForm(false);
      await fetchAgents();
    } catch (error) {
      logger.error("Error creating agent:", error);
      alert(error.response?.data?.detail || "Error creating agent");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAgent = async (agentId, agentName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${agentName}?`)) return;
    
    try {
      await axios.post(`${API}/agent/delete`, {
        agent_id: agentId,
        requester_id: agent.id
      });
      
      alert("Agent deactivated successfully");
      await fetchAgents();
    } catch (error) {
      logger.error("Error deleting agent:", error);
      alert(error.response?.data?.detail || "Error deleting agent");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
              <Users className="w-8 h-8 text-white" />
            </div>
            Agent Management
          </h1>
          <p className="text-gray-500 mt-2">Create and manage your support team</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          size="lg"
          className="bg-gradient-to-r from-[var(--g6-primary)] to-[#008A44] hover:shadow-lg transition-all duration-200 h-12 px-6"
        >
          <UserPlus className="w-5 h-5 mr-2" />
          {showCreateForm ? "Cancel" : "Add New Agent"}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Agents</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-1">{agents.length}</h3>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-100 bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Online Now</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-1">
                  {agents.filter(a => a.is_online).length}
                </h3>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <div className="w-6 h-6 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-100 bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Admins</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-1">
                  {agents.filter(a => a.role === 'tenant_admin').length}
                </h3>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <span className="text-2xl">👑</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Agent Form */}
      {showCreateForm && (
        <Card className="border-2 border-[var(--g6-primary)] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[var(--g6-primary)]" />
              Create New Agent
            </CardTitle>
            <CardDescription>Add a new team member to handle customer conversations</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleCreateAgent} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Full Name *</label>
                  <Input
                    value={newAgent.name}
                    onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                    placeholder="John Doe"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Email Address * (used for login)</label>
                  <Input
                    type="email"
                    value={newAgent.email}
                    onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                    placeholder="john@company.com"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Role *</label>
                  <select
                    value={newAgent.role}
                    onChange={(e) => setNewAgent({ ...newAgent, role: e.target.value })}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 h-11 focus:outline-none focus:border-[var(--g6-primary)] transition-colors"
                    required
                  >
                    <option value="agent">Agent - Support agent</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Only one admin is allowed per tenant</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Set Password *</label>
                  <Input
                    type="password"
                    value={newAgent.password}
                    onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })}
                    placeholder="Enter password"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Confirm Password *</label>
                  <Input
                    type="password"
                    value={newAgent.confirmPassword}
                    onChange={(e) => setNewAgent({ ...newAgent, confirmPassword: e.target.value })}
                    placeholder="Confirm password"
                    className="h-11"
                    required
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Mail className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-blue-800">
                  Agent credentials will be created and can be shared manually. Email integration coming soon.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 h-11 bg-gradient-to-r from-[var(--g6-primary)] to-[#008A44] hover:shadow-lg transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Creating Agent...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Agent
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                  className="h-11 px-6 border-2"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Agents Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Team Members ({agents.length})
          </h2>
        </div>

        {agents.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="p-12 text-center">
              <div className="flex flex-col items-center">
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <Users className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No agents yet</h3>
                <p className="text-gray-500 mb-4">Get started by creating your first agent</p>
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-gradient-to-r from-[var(--g6-primary)] to-[#008A44]"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create First Agent
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((ag) => {
              const initials = ag.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
              const gradients = {
                tenant_admin: 'from-purple-500 to-pink-600',
                agent: 'from-[var(--g6-primary)] to-[#008A44]'
              };

              return (
                <Card key={ag.id} className="border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradients[ag.role] || gradients.agent} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                        {initials}
                      </div>
                      {ag.is_online && (
                        <div className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded-full">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-xs font-semibold text-green-700">Online</span>
                        </div>
                      )}
                    </div>

                    <h3 className="font-bold text-lg text-gray-900 mb-1">{ag.name}</h3>
                    <p className="text-sm text-gray-500 mb-4 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {ag.email}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge
                        variant={ag.role === 'tenant_admin' ? 'default' : 'secondary'}
                        className={`${ag.role === 'tenant_admin' ? 'bg-purple-500' : 'bg-gray-600'} text-white`}
                      >
                        {ag.role === 'tenant_admin' ? '👑 Admin' : '👤 Agent'}
                      </Badge>

                      {!ag.is_online && (
                        <Badge variant="outline" className="border-gray-300">
                          Offline
                        </Badge>
                      )}

                      {!ag.is_active && (
                        <Badge variant="destructive">
                          Deactivated
                        </Badge>
                      )}
                    </div>

                    {ag.id !== agent.id && ag.is_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAgent(ag.id, ag.name)}
                        className="w-full text-red-600 hover:bg-red-50 hover:border-red-300 border-2 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Deactivate Agent
                      </Button>
                    )}

                    {ag.id === agent.id && (
                      <Badge variant="outline" className="w-full justify-center py-2 border-blue-200 bg-blue-50 text-blue-700">
                        This is you
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentManagement;
